import { createServerFn } from "@tanstack/react-start";
import { sanityClient } from "./sanity";

export const INDUSTRY_TAGS = [
  "DeFi",
  "Agentic CMO",
  "Agentic Web",
  "Agentic Commerce",
  "AI Governance",
  "Agent Security & Privacy",
  "Cost of AI",
  "AI Macro Trends",
  "Circular Commerce",
  "Climate Tech",
  "Modern Consumer",
] as const;

export type IndustryTag = (typeof INDUSTRY_TAGS)[number];

type SanityImageWithAsset = { asset?: { url?: string } };

export type MarketReport = {
  _id: string;
  _updatedAt?: string;
  title: string;
  slug?: { current: string };
  publishedAt: string;
  authorName: string;
  authorSocialUrl: string;
  summary?: string;
  industryTag: IndustryTag;
  // From Sanity: hero image with derefed asset url
  heroImage?: SanityImageWithAsset;
  // Optional pre-built absolute URL (not populated from Sanity).
  heroImageUrl?: string;
  // True when the report has a Portable Text body. Cheaply projected in the
  // list query (without fetching `content`) so sitemap/feed callers can mirror
  // the detail page's `noindex: !hasBody` rule. Always true on the detail query.
  hasBody?: boolean;
  // Portable Text body — only fetched for the detail page.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches BlogPost.body; `any` is required for the server-fn serialization check.
  content?: any;
  // SEO fields — only fetched for the detail page.
  meta_title?: string;
  meta_description?: string;
  keywords?: string[];
  canonical_url?: string;
  og_title?: string;
  og_description?: string;
  og_image?: SanityImageWithAsset;
  twitter_title?: string;
  twitter_description?: string;
  twitter_card_type?: "summary" | "summary_large_image" | "player" | "app";
};

const PROJECTION = `{
  _id, _updatedAt, title, slug, publishedAt, authorName, authorSocialUrl,
  summary, industryTag,
  heroImage{ asset->{ url, metadata { dimensions } } },
  "hasBody": count(content[]) > 0
}`;

// Detail page also needs the body + SEO/OG fields. `schema_json_ld` is deliberately
// not projected: the NewsArticle JSON-LD is generated from the document's fields
// (see src/routes/market-reports.$slug.tsx).
const DETAIL_PROJECTION = `{
  _id, _updatedAt, title, slug, publishedAt, authorName, authorSocialUrl,
  summary, industryTag,
  heroImage{ asset->{ url, metadata { dimensions } } },
  content,
  meta_title, meta_description, keywords, canonical_url,
  og_title, og_description,
  og_image{ asset->{ url } },
  twitter_title, twitter_description, twitter_card_type
}`;

export const getAllMarketReports = createServerFn({ method: "GET" }).handler(
  async (): Promise<MarketReport[]> => {
    try {
      const results = await sanityClient.fetch<MarketReport[]>(
        `*[_type == "marketReport" && defined(slug.current)] | order(publishedAt desc)${PROJECTION}`,
      );
      return results ?? [];
    } catch (err) {
      console.error("Sanity marketReport fetch failed:", err);
      return [];
    }
  },
);

export const getMarketReportBySlug = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }): Promise<MarketReport | null> => {
    try {
      const result = await sanityClient.fetch<MarketReport | null>(
        `*[_type == "marketReport" && slug.current == $slug][0]${DETAIL_PROJECTION}`,
        { slug: data.slug },
      );
      return result ?? null;
    } catch (err) {
      console.error("Sanity marketReport fetch failed:", err);
      return null;
    }
  });

/** Used by the news sitemap loader (≤72h rolling). */
export function filterLast72h(reports: MarketReport[]): MarketReport[] {
  const cutoff = Date.now() - 72 * 60 * 60 * 1000;
  return reports.filter((r) => {
    const t = Date.parse(r.publishedAt);
    return Number.isFinite(t) && t >= cutoff;
  });
}
